import { useQuery } from '@tanstack/react-query';
import { Building2, CheckCircle2, Clock, AlertCircle, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/api';

function StatCard({ icon: Icon, label, value, color }: {
  icon: any; label: string; value: number | string; color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={20}/>
      </div>
      <div>
        <div className="text-2xl font-bold text-navy">{value}</div>
        <div className="text-xs text-gray-400 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn:  () => adminApi.stats().then(r => r.data),
  });

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Platform Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">Overview of all registered hospitals</p>
        </div>
        <Link to="/admin/hospitals/new"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange text-navy
                     font-semibold text-sm hover:bg-orange-dark transition-colors">
          <Plus size={16}/>
          New Hospital
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 h-20 animate-pulse"/>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Building2}    label="Total Hospitals"   value={data?.total     ?? 0} color="bg-navy/10 text-navy"/>
          <StatCard icon={CheckCircle2} label="Active"            value={data?.active    ?? 0} color="bg-clinical-success/10 text-clinical-success"/>
          <StatCard icon={Clock}        label="Pending / Review"  value={(data?.pending ?? 0) + (data?.underReview ?? 0)} color="bg-orange/10 text-orange"/>
          <StatCard icon={AlertCircle}  label="Under Review"      value={data?.underReview ?? 0} color="bg-clinical-info/10 text-clinical-info"/>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-navy">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/admin/hospitals/new"
            className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-orange/40
                       hover:border-orange hover:bg-orange/5 transition-colors group">
            <div className="w-9 h-9 rounded-lg bg-orange/10 flex items-center justify-center
                            group-hover:bg-orange/20 transition-colors">
              <Plus size={18} className="text-orange"/>
            </div>
            <div>
              <div className="text-sm font-medium text-navy">Register Hospital</div>
              <div className="text-xs text-gray-400">5-step onboarding wizard</div>
            </div>
          </Link>
          <Link to="/admin/hospitals"
            className="flex items-center gap-3 p-4 rounded-xl border border-gray-200
                       hover:border-navy/30 hover:bg-navy/5 transition-colors group">
            <div className="w-9 h-9 rounded-lg bg-navy/10 flex items-center justify-center">
              <Building2 size={18} className="text-navy"/>
            </div>
            <div>
              <div className="text-sm font-medium text-navy">Manage Hospitals</div>
              <div className="text-xs text-gray-400">View all, approve, suspend</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
